import React from "react";

const TechStack = () => {
  const stats = [
    { value: "3+ yrs", label: "Engineering experience" },
    { value: "4", label: "Product and client engagements" },
    { value: "Full stack", label: "Frontend-heavy delivery" },
  ];

  const scopeSummary = [
    {
      title: "Frontend engineering",
      text: "React, Next.js, TypeScript, dashboards, design systems, and responsive product UI.",
    },
    {
      title: "Backend-connected work",
      text: "REST APIs, gRPC-backed flows, BFF layers, Supabase, webhooks, and data-driven features.",
    },
    {
      title: "Product delivery",
      text: "CMS, SEO, analytics, automation, testing workflows, and production feature ownership.",
    },
  ];

  const timeline = [
    {
      category: "Client Product Engineering",
      role: "Software Engineer - Contract",
      company: "Code Sentinels / Runa HR",
      period: "Dec 2023 - Dec 2025",
      scope: "YC-backed HRMS platform",
      summary:
        "Built production HRMS workflows across payroll management, payroll configuration, dashboards, and internal product modules.",
      highlights: [
        "Developed React and Next.js modules with TypeScript, Material UI, React Query, and Jotai.",
        "Integrated workflows with REST APIs, gRPC-backed services, BFF layers, shared frontend libraries, and i18n.",
        "Supported quality and delivery with Cypress, Vitest, React Testing Library, Vite, and Elasticsearch-powered features.",
      ],
      stack: [
        "Next.js",
        "React",
        "TypeScript",
        "Material UI",
        "React Query",
        "Jotai",
        "REST APIs",
        "gRPC",
        "BFF",
        "Cypress",
      ],
    },
    {
      category: "Product Engineering",
      role: "Frontend Developer",
      company: "Qyubi Tech",
      period: "Apr 2023 - Nov 2023",
      scope: "Vehicle tracking CRM",
      summary:
        "Built and maintained operational CRM interfaces, including map-driven workflows for tracking, routing, and geofencing.",
      highlights: [
        "Implemented Leaflet map features for geofencing, route drawing, and location-based workflows.",
        "Created responsive screens and reusable UI components across product workflows.",
        "Handled debugging, frontend fixes, and interface improvements in a team production codebase.",
      ],
      stack: ["React", "JavaScript", "HTML", "CSS", "Leaflet", "Responsive UI"],
    },
    {
      category: "Product Engineering",
      role: "Product Engineer",
      company: "College Cosmos",
      period: "Jan 2026 - Present",
      scope: "Admissions and counselling platform",
      summary:
        "Own product execution for a college discovery and admissions platform, covering frontend, CMS content, SEO, analytics, and lead-generation workflows.",
      highlights: [
        "Built the product with Next.js, TypeScript, Strapi CMS, and Tailwind CSS.",
        "Set up content workflows, SEO foundations, Google Tag Manager, and Meta Pixel tracking, reaching 15.6K impressions and 131 clicks in a recent 28-day Search Console view.",
        "Designed the platform around student discovery, counselling flows, and measurable acquisition.",
      ],
      stack: [
        "Next.js",
        "TypeScript",
        "Strapi CMS",
        "Tailwind CSS",
        "SEO",
        "GTM",
        "Meta Pixel",
      ],
    },
    {
      category: "Independent Product",
      role: "Product / Full Stack Engineer",
      company: "Glamnexx",
      period: "Active Development",
      scope: "SaaS for appointment-led service businesses",
      summary:
        "Developing SaaS workflows for service businesses, combining dashboards, Supabase-backed flows, webhooks, WhatsApp automation, templates, and AI assistant flows.",
      highlights: [
        "Building product screens, operational dashboards, and customer-facing workflows with Next.js and TypeScript.",
        "Connecting backend workflows through Supabase, webhooks, Meta APIs, and automation flows.",
        "Completed Meta Tech Provider access verification and implemented onboarding for business assets and WhatsApp templates.",
        "Designing reusable templates and AI-assisted workflows for appointment-led business operations.",
      ],
      stack: [
        "Next.js",
        "TypeScript",
        "Supabase",
        "Webhooks",
        "Meta APIs",
        "AI Flows",
      ],
    },
  ];

  const capabilityGroups = [
    {
      title: "Application Frontend",
      desc: "Production interfaces, dashboard UX, shared components, state, and responsive implementation.",
      skills: [
        "React",
        "Next.js",
        "TypeScript",
        "Material UI",
        "Tailwind CSS",
        "shadcn/ui",
      ],
    },
    {
      title: "Backend & APIs",
      desc: "Backend-connected product flows across APIs, BFF layers, data services, and automation triggers.",
      skills: [
        "REST APIs",
        "gRPC",
        "BFF Layer",
        "Supabase",
        "Webhooks",
        "Microservices",
      ],
    },
    {
      title: "Quality & Tooling",
      desc: "Testing, debugging, build tooling, and maintainable frontend workflows for team codebases.",
      skills: [
        "Cypress",
        "Vitest",
        "React Testing Library",
        "Vite",
        "Git",
        "Monorepo",
      ],
    },
    {
      title: "Growth Systems",
      desc: "CMS, SEO, analytics, tracking, WhatsApp workflows, and lead-management product operations.",
      skills: [
        "Strapi CMS",
        "SEO",
        "GTM",
        "Meta Pixel",
        "Lead Generation",
        "WhatsApp Automation",
        "AI Assistant",
      ],
    },
  ];

  return (
    <section
      name="experience"
      className="bg-[#0b0f12] px-5 py-24 text-white lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-[0.38fr_0.62fr] lg:items-start">
          <aside className="lg:sticky lg:top-28">
            <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-emerald-300">
              Experience
            </p>
            <h2 className="mt-4 text-3xl font-extrabold leading-tight sm:text-5xl">
              Frontend-heavy software engineering across products, APIs, and
              workflows.
            </h2>
            <p className="mt-5 text-lg leading-8 text-zinc-400">
              I work primarily in React and Next.js, while also handling API
              integration, backend-connected workflows, testing, analytics, CMS,
              and product delivery.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
              {stats.map(({ value, label }) => (
                <div
                  key={label}
                  className="rounded-md border border-white/10 bg-white/[0.035] p-5"
                >
                  <p className="text-2xl font-extrabold text-white">{value}</p>
                  <p className="mt-2 text-sm font-semibold leading-5 text-zinc-500">
                    {label}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8 border-t border-white/10 pt-6">
              <h3 className="text-base font-extrabold text-white">
                Delivery Scope
              </h3>
              <div className="mt-5 space-y-5">
                {scopeSummary.map(({ title, text }) => (
                  <div key={title} className="border-l border-emerald-300/30 pl-4">
                    <p className="font-bold text-zinc-100">{title}</p>
                    <p className="mt-2 text-sm leading-6 text-zinc-500">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          <div className="relative">
            <div className="absolute bottom-2 left-[10px] top-2 hidden w-px bg-white/10 sm:block" />
            <div className="space-y-6">
              {timeline.map(
                ({
                  category,
                  role,
                  company,
                  period,
                  scope,
                  summary,
                  highlights,
                  stack,
                }) => (
                  <article
                    key={`${company}-${role}`}
                    className="relative rounded-md border border-white/10 bg-[#111719] p-5 shadow-2xl shadow-black/20 sm:ml-8 sm:p-6"
                  >
                    <span className="absolute -left-[2.3rem] top-8 hidden h-5 w-5 rounded-full border border-emerald-300/60 bg-[#0b0f12] sm:block">
                      <span className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-300" />
                    </span>

                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-emerald-300">
                          {category}
                        </p>
                        <h3 className="mt-3 text-2xl font-extrabold leading-tight text-white">
                          {role}
                        </h3>
                        <p className="mt-2 text-lg font-bold text-zinc-300">
                          {company}
                        </p>
                        <p className="mt-2 text-sm font-semibold text-zinc-500">
                          {scope}
                        </p>
                      </div>
                      <p className="w-fit rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-sm font-bold text-zinc-300">
                        {period}
                      </p>
                    </div>

                    <p className="mt-5 leading-7 text-zinc-300">{summary}</p>

                    <ul className="mt-5 grid gap-3">
                      {highlights.map((highlight) => (
                        <li
                          key={highlight}
                          className="flex gap-3 text-sm leading-6 text-zinc-400"
                        >
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-300" />
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-6 flex flex-wrap gap-2">
                      {stack.map((item) => (
                        <span
                          key={item}
                          className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-sm font-bold text-zinc-300"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </article>
                )
              )}
            </div>
          </div>
        </div>

        <div className="mt-16 border-t border-white/10 pt-10">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-emerald-300">
                Technical Scope
              </p>
              <h3 className="mt-2 text-2xl font-extrabold text-white">
                Capabilities I bring into delivery
              </h3>
            </div>
            <p className="max-w-xl text-sm leading-6 text-zinc-500">
              The focus is frontend engineering, but the work extends into
              backend integrations, product systems, quality, and growth tooling.
            </p>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {capabilityGroups.map(({ title, desc, skills }) => (
              <article
                key={title}
                className="rounded-md border border-white/10 bg-white/[0.035] p-5"
              >
                <h4 className="text-lg font-extrabold text-white">{title}</h4>
                <p className="mt-3 text-sm leading-6 text-zinc-500">{desc}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {skills.map((skill) => (
                    <span
                      key={skill}
                      className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-sm font-bold text-zinc-300"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechStack;
